import round2Decimals from "../../../../../Components/round2Decimals";

// % de avance de un nodo hoja: cuánto se ha producido vs. lo presupuestado en esa rama.
const avanceHoja = (nodo) => {
  const total = nodo.cantidadTotal || nodo.cantidad || 0;
  if (total <= 0) return 0;
  const producida = nodo.cantidadProducida || 0;
  return Math.min(100, Math.max(0, round2Decimals((producida / total) * 100)));
};

const costoBaseDe = (nodo, calcularConImpuestos) =>
  (calcularConImpuestos ? nodo.costoTotalCImpuestos : nodo.costoTotal) || 0;

// % de avance de un nodo "project": promedio ponderado por costo de sus hijos visibles.
// Se pondera por costo (no por cantidad) porque cantidadTotal no es una unidad homogénea
// entre ramas del árbol (m³, kg, piezas mezclados); el costo sí lo es.
const calcularAvance = (nodo, calcularConImpuestos) => {
  const hijosVisibles = (nodo.children || []).filter((h) => !h.insumoMatriz);

  if (hijosVisibles.length === 0) {
    return { avance: avanceHoja(nodo), costoBase: costoBaseDe(nodo, calcularConImpuestos) };
  }

  let sumaPonderada = 0;
  let sumaCosto = 0;
  const avancesHijos = [];
  for (const hijo of hijosVisibles) {
    const resultado = calcularAvance(hijo, calcularConImpuestos);
    avancesHijos.push(resultado.avance);
    sumaPonderada += resultado.avance * resultado.costoBase;
    sumaCosto += resultado.costoBase;
  }

  const avance = sumaCosto > 0
    ? round2Decimals(sumaPonderada / sumaCosto)
    : round2Decimals(avancesHijos.reduce((acc, a) => acc + a, 0) / avancesHijos.length);

  return { avance, costoBase: costoBaseDe(nodo, calcularConImpuestos) || sumaCosto };
};

const resolverNombre = (nodo, todasMatrices) => {
  if (nodo.tipo === 1 && nodo.nombre) {
    const matriz = (todasMatrices || []).find(
      (m) => m._id.toString() === nodo.nombre.toString()
    );
    return matriz ? matriz.descripcion : nodo.nombre;
  }
  return nodo.nombre;
};

// Árbol de tasks para dhtmlx-gantt, filtrando nodos insumoMatriz
export function construirTasksGantt(partidas, todasMatrices, calcularConImpuestos) {
  const tasks = [];

  const recorrer = (nodo, path, parentId) => {
    if (nodo.insumoMatriz) return;

    // Prefijo "p" para no chocar con el id 0 reservado de dhtmlx
    const id = "p" + path.join("-");
    const hijosVisibles = (nodo.children || []).filter((h) => !h.insumoMatriz);
    const esProject = hijosVisibles.length > 0;
    const { avance } = calcularAvance(nodo, calcularConImpuestos);

    tasks.push({
      id,
      text: resolverNombre(nodo, todasMatrices),
      start_date: nodo.start ? new Date(nodo.start) : new Date(),
      end_date: nodo.end ? new Date(nodo.end) : moment_addDia(nodo.start),
      progress: avance / 100,
      type: esProject ? "project" : "task",
      parent: parentId || 0,
      open: true,
      // Ruta posicional del nodo dentro de `partidas`, para mandarla tal cual al backend
      // sin tener que re-parsear el id por guiones.
      _path: path,
    });

    hijosVisibles.forEach((hijo, i) => recorrer(hijo, [...path, i], id));
  };

  (partidas || []).forEach((raiz, i) => recorrer(raiz, [i], 0));

  return { data: tasks, links: [] };
}

// Evita traer moment solo para "hoy + 1 día" cuando no hay start.
function moment_addDia(start) {
  const base = start ? new Date(start) : new Date();
  const masUnDia = new Date(base);
  masUnDia.setDate(masUnDia.getDate() + 1);
  return masUnDia;
}

// Recorrido plano con nivel de indentación, para exportar a Excel
export function construirFilasExportGantt(partidas, todasMatrices, calcularConImpuestos) {
  const filas = [];

  const recorrer = (nodo, nivel) => {
    if (nodo.insumoMatriz) return;

    const hijosVisibles = (nodo.children || []).filter((h) => !h.insumoMatriz);
    const { avance } = calcularAvance(nodo, calcularConImpuestos);
    const start = nodo.start ? new Date(nodo.start) : new Date();
    const end = nodo.end ? new Date(nodo.end) : moment_addDia(nodo.start);
    const dias = Math.max(1, Math.round((end.getTime() - start.getTime()) / 86400000));

    filas.push({
      nivel,
      nombre: resolverNombre(nodo, todasMatrices),
      esMatriz: nodo.tipo === 1,
      esGrupo: hijosVisibles.length > 0,
      start,
      end,
      dias,
      avance,
    });

    hijosVisibles.forEach((hijo) => recorrer(hijo, nivel + 1));
  };

  (partidas || []).forEach((raiz) => recorrer(raiz, 0));

  return filas;
}
